import { useEffect } from "react";

/**
 * Loads the next page of an infinite-scroll list while rows filtered out on the client leave it
 * with nothing to scroll.
 *
 * `useInfiniteScroll` loads more only when the list is scrolled to its end. A list that leaves out
 * some of what the server sent — a people picker leaving out the client's own firm (#6091) — can
 * be left short of its container, or empty, and then it never scrolls and the rows on later pages
 * are never reached. This keeps loading while the filter has removed rows and the container does
 * not overflow.
 *
 * A failed page stops it: retrying belongs to the list's own Retry action, not to a loop of
 * requests that would clear the error each time it starts again.
 *
 * @param filterRemovedRows - Whether the client-side filter left out any loaded row.
 * @param hasMore - Whether the server has another page.
 * @param isLoading - Whether a page is loading now.
 * @param hasError - Whether the last page failed.
 * @param loadMore - Loads the next page.
 * @param rootRef - The list's scrollable container.
 */
export function useLoadPastFilteredRows(
  filterRemovedRows: boolean,
  hasMore: boolean,
  isLoading: boolean,
  hasError: boolean,
  loadMore: () => Promise<void> | void,
  rootRef: React.RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!filterRemovedRows || !hasMore || isLoading || hasError) return;
    const pane = rootRef.current;
    if (pane && pane.scrollHeight > pane.clientHeight) return;
    void loadMore();
  }, [filterRemovedRows, hasMore, isLoading, hasError, loadMore, rootRef]);
}
